import {wait} from '@cibsite/cib-helpers';

import YaLogo from '@/assets/images/icons/y-map/user-y-marker.svg';
import LocationLogo from '@/assets/images/icons/y-map/location.svg';
import LocationOffLogo from '@/assets/images/icons/y-map/location-disable.svg';

const YM_SCRIPT_ID = '__ym-script__';
const BASE_YM_API_PATH = 'https://api-maps.yandex.ru';
const YM_ERRORS = {
    DOCUMENT: 'document is not defined',
    YMAPS: 'ymaps is not defined',
};

export default class YandexMapBuilder {
    map = null;

    constructor({apiKey, mapId, mapInstance = null}) {
        this.apiKey = apiKey;
        this.mapId = mapId;

        if (mapInstance) {
            this.map = mapInstance;
        }
    }

    /**
     * @param {object} props
     * @param {string} props.coordorder
     * @param {string} props.version
     * @param {object} props.mapOptions
     * @throws {Error} ymaps is not defined
     * @return {Promise<object>}
     * @public
     */
    async buildMap({coordorder, version, mapOptions}) {
        if (this.map) {
            this.map.destroy();
            this.map = null;
        }

        this.#appendYmScript({
            coordorder,
            version,
        });

        const ymaps = await this.#getYmapsInstance();

        if (!ymaps) {
            throw new Error(YM_ERRORS.YMAPS);
        }

        return new Promise((resolve) => {
            return ymaps.ready(
                this.#initYm.bind(this, {
                    ymaps,
                    mapOptions,
                    resolve,
                }),
            );
        });
    }

    /**
     * @param {object} props
     * @param {object} props.options
     * @param {[number, number]} props.coordinates
     * @throws {Error} ymaps is not defined
     * @return {Promise<object>}
     * @public
     */
    async setMapMarker({options, events}) {
        const ymaps = await this.#getYmapsInstance();

        if (!ymaps?.Placemark) {
            throw new Error(YM_ERRORS.YMAPS);
        }

        const mapPlacemark = new ymaps.Placemark(
            options.coordinates,
            {
                hintContent: options.hintContent,
                balloonContent: options.balloonContent,
                iconContent: options.iconContent,
            },
            {
                iconLayout: options.iconLayout,
                iconImageHref: options.iconImageHref,
                iconImageSize: options.iconImageSize,
                iconImageOffset: options.iconImageOffset,
                preset: options.preset,
            },
        );

        this.map?.geoObjects?.add(mapPlacemark);

        if (!events && !(events instanceof Array)) {
            return;
        }

        for (const markerEvent of events) {
            mapPlacemark.events.add(
                markerEvent.type,
                (event) => markerEvent.callback({event, payload: markerEvent?.payload}),
            );
        }

        return mapPlacemark;
    }

    /**
     * @param {object} options
     * @param {array} geoObjects
     * @throws {Error} ymaps is not defined
     * @return {Promise<object>}
     * @public
     */
    async setClusters(options, geoObjects) {
        const ymaps = await this.#getYmapsInstance();

        if (!ymaps?.Clusterer) {
            throw new Error(YM_ERRORS.YMAPS);
        }

        let iconContentLayout = options.iconLayout || null;

        if (options.iconCustomTemplate) {
            iconContentLayout = ymaps.templateLayoutFactory.createClass(options.iconCustomTemplate);
        }

        const clusterer = new ymaps.Clusterer({
            clusterIcons: options.icons,
            clusterIconContentLayout: iconContentLayout,
            clusterIconContentOffset: options.iconContentOffset,
            clusterBalloonLayout: options.balloonLayout || ymaps.templateLayoutFactory?.createClass(''),
            clusterBalloonShadow: options.balloonShadow || false,
            clusterDisableClickZoom: options.disableClickZoom || false,
            clusterOpenBalloonOnClick: options.openBalloonOnClick || false,
        });

        this.map?.geoObjects?.add(clusterer);

        clusterer.add(await Promise.all(geoObjects));

        return clusterer;
    }

    /**
     * @param {object} options
     * @param {object} options.position
     * @param {number} options.position.top
     * @param {number} options.position.left
     * @param {number} options.zoomAfterClick
     * @return {Promise<void>}
     * @public
     */
    async setGeolocation(options = {}) {
        if (typeof window === 'undefined' || !window?.navigator?.geolocation) {
            return;
        }

        const ymaps = await this.#getYmapsInstance();
        const coordinates = await this.#getUserCoords();

        coordinates && this.setMapMarker({
            options: {
                coordinates,
                iconLayout: 'default#image',
                iconImageHref: YaLogo,
                iconImageSize: [24, 24],
                iconImageOffset: [-12, -12],
            },
        });

        const geolocationControl = new ymaps.control.Button({
            options: {
                selectOnClick: false,
                ...options,
            },
            data: {
                image: coordinates ? LocationLogo : LocationOffLogo,
                title: coordinates ? 'Определить местоположение' : 'Нет доступа к геолокации',
            },
        });

        this.map.controls.add(geolocationControl);

        coordinates && options.zoomAfterClick && geolocationControl.events.add('press', async() => {
            const updatedCoords = await this.#getUserCoords();

            updatedCoords && this.map.panTo(updatedCoords).then(() => {
                this.map.setZoom(options.zoomAfterClick, {duration: 1000});
            });
        });
    }

    /**
     * @return {Promise<number[] | null>}
     * @private
     */
    #getUserCoords() {
        return new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
                ({coords}) => resolve([coords.latitude, coords.longitude]),
                () => resolve(null),
                {maximumAge: 60000, timeout: 5000, enableHighAccuracy: true},
            );
        });
    }

    /**
     * @param {object} props
     * @param {string} props.coordorder
     * @param {object} props.version
     * @throws {Error} document is not defined
     * @return {void}
     * @private
     */
    #appendYmScript({coordorder, version}) {
        const scriptId = `${YM_SCRIPT_ID}`;
        const isScriptExist = this.#isYmScriptExist(scriptId);

        if (isScriptExist) {
            return;
        }

        const params = new URLSearchParams({coordorder}).toString();
        const src = `${BASE_YM_API_PATH}/${version}/?${params}&lang=ru_RU`;

        if (!window?.document) {
            throw new Error(YM_ERRORS.DOCUMENT);
        }

        const script = document.createElement('script');
        script.setAttribute('src', src);
        script.setAttribute('type', 'text/javascript');
        script.setAttribute('id', scriptId);
        document.head.appendChild(script);
    }

    /**
         * @param {string} scriptId
         * @return {boolean}
         * @private
         */
    #isYmScriptExist(scriptId) {
        return Boolean(document.getElementById(scriptId)) ?? false;
    }

    /**
         * @return {Promise<object | null>}
         * @private
         */
    #getYmapsInstance() {
        const attempt = 0;

        return new Promise((resolve) => {
            const interval = setInterval(() => {
                const {ymaps} = window;

                if (ymaps || attempt > 5) {
                    clearInterval(interval);
                    resolve(ymaps);
                }
            }, 100);
        });
    }

    /**
         * @param {object} props
         * @param {object} props.ymaps
         * @param {object} props.mapOptions
         * @return {void}
         * @private
         */
    #initYm({ymaps, resolve, mapOptions = {}}) {
        wait(1000).then(() => {
            this.map = new ymaps.Map(this.mapId, mapOptions);

            resolve(this.map);
        });
    }
}
